import { Injectable } from '@angular/core';
import { ApiService } from '@quorum/api';
import { GmSltData } from '@quorum/models/xs-misc';
import { MediaStateService } from '@quorum/sha-media';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GmSltService {
  constructor(private apiService: ApiService, private mediaStateService: MediaStateService) {}

  defaultGmSltData: GmSltData = {
    roData: {
      mobileDeviceCheckIn: {
        wasMobileCheckIn: null,
        mobileCheckInDate: null,
      },
      serviceVisitTracking: null,
      electronicEstimate: {
        wasEstimateSent: 'N',
        methodOfEstimateSent: null,
      },
      media: {
        mediaUploaded: 'N',
        numberOfVideos: '0',
        numberOfPhotos: '0',
      },
      postBooklet: {
        postBookletPresented: 'N',
        methodOfPostBookletSent: null,
      },
    },
    menuData: [],
    asrData: [],
    workOrderId: null,
    createdTimestamp: null,
  };

  getGmSltByWorkOrderId(destinationId: string, workOrderId: string): Observable<GmSltData> {
    return this.apiService
      .get(`v/1/fixed-operations/gm-slt/${destinationId}/${workOrderId}`)
      .pipe(map((gmSltData) => gmSltData[0]));
  }

  createGmSltRecord(workOrderId: number): Observable<GmSltData> {
    const isTouchscreen = this.isTouchscreenDevice();
    const updatedGmSltData = {
      ...this.defaultGmSltData,
      roData: {
        ...this.defaultGmSltData.roData,
        mobileDeviceCheckIn: {
          wasMobileCheckIn: isTouchscreen ? 'Y' : 'N',
          mobileCheckInDate: isTouchscreen ? new Date() : null,
        },
      },
      workOrderId: workOrderId,
    };

    return this.apiService.post(`v/1/fixed-operations/gm-slt`, updatedGmSltData);
  }

  updateGmSltRecord(gmSlt: GmSltData): Observable<GmSltData> {
    return this.apiService.put(`v/1/fixed-operations/gm-slt/${gmSlt.workOrderId}`, gmSlt);
  }

  /**
   * Utility function to determine if the users device is touchscreen (tablet/mobile) as recommended
   * by MDN web docs (https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent)
   */
  isTouchscreenDevice() {
    let isTouchscreen = false;

    if ('maxTouchPoints' in navigator) {
      isTouchscreen = navigator.maxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        isTouchscreen = !!mQ.matches;
      } else if ('orientation' in window) {
        isTouchscreen = true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        const UA = navigator.userAgent;
        isTouchscreen =
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
      }
    }

    return isTouchscreen;
  }

  getOrCreateGmSltData(destination: string, workOrderId: string): Observable<GmSltData> {
    return this.getGmSltByWorkOrderId(destination, workOrderId).pipe(
      switchMap((slt: GmSltData) => {
        if (!slt) {
          return this.createGmSltRecord(Number(workOrderId));
        }
        return of(slt);
      }),
      take(1)
    );
  }
}
